import React from "react";
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  TagIcon,
  LightBulbIcon,
} from "@heroicons/react/24/outline";

const LEVEL_CONFIG = {
  RESTRICTED:   { bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200",    bar: "bg-red-500"    },
  CONFIDENTIAL: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", bar: "bg-orange-500" },
  PRIVATE:      { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200", bar: "bg-yellow-500" },
  PUBLIC:       { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200",  bar: "bg-green-500"  },
};

const levelConfig = (level) =>
  LEVEL_CONFIG[level] ?? LEVEL_CONFIG.PUBLIC;

const riskColor = (score) => {
  if (score >= 75) return "bg-red-500";
  if (score >= 50) return "bg-orange-500";
  if (score >= 25) return "bg-yellow-500";
  return "bg-green-500";
};

const ClassificationTab = ({ classification }) => {
  if (!classification) return null;
  const { signals = [], reasons = [] } = classification;
  const cfg = levelConfig(classification.classification);
  const confidence = Math.round((classification.confidence ?? 0) * 100);

  return (
    <div className="space-y-6">

      {/* Top summary row */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-gray-100">
        <div>
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Asset</p>
          <p className="text-base font-semibold text-gray-900">{classification.asset_name}</p>
        </div>
        <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
          <ShieldCheckIcon className="w-4 h-4" />
          {classification.classification}
        </span>
      </div>

      {/* Review warning */}
      {classification.needs_review && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 font-medium">
            This asset requires manual review — confidence score is below the threshold.
          </p>
        </div>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Risk Score */}
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Risk Score</p>
          <p className="text-3xl font-bold text-gray-900">
            {classification.risk_score}
            <span className="text-sm font-normal text-gray-400"> / 100</span>
          </p>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${riskColor(classification.risk_score)}`}
              style={{ width: `${classification.risk_score}%` }}
            />
          </div>
        </div>

        {/* Confidence */}
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Confidence</p>
          <p className="text-3xl font-bold text-gray-900">{confidence}%</p>
          <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${confidence}%`, backgroundColor: "var(--app-primary-color, #26256b)" }}
            />
          </div>
        </div>

        {/* Review Status */}
        <div className="border border-gray-200 rounded-xl p-4 space-y-3">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Review Status</p>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ${
            classification.needs_review
              ? "bg-amber-50 text-amber-700"
              : "bg-green-50 text-green-700"
          }`}>
            <CheckCircleIcon className="w-4 h-4" />
            {classification.needs_review ? "Needs Review" : "Approved"}
          </div>
        </div>
      </div>

      {/* Signals */}
      {signals.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <TagIcon className="w-4 h-4 text-gray-400" />
            <p className="text-sm font-semibold text-gray-700">Signals Detected</p>
            <span className="ml-auto text-xs text-gray-400">{signals.length} signal{signals.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {signals.map((signal) => (
              <span
                key={signal}
                className="px-3 py-1 rounded-full text-xs font-medium text-white"
                style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
              >
                {signal.replaceAll("_", " ")}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Reasons */}
      {reasons.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <LightBulbIcon className="w-4 h-4 text-gray-400" />
            <p className="text-sm font-semibold text-gray-700">Classification Reasons</p>
          </div>
          <ul className="space-y-2">
            {reasons.map((reason, idx) => (
              <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-600">
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: "var(--app-primary-color, #26256b)" }}
                />
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  );
};

export default ClassificationTab;
